import React, { useState } from 'react';
import _ from 'lodash';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Checkbox from '@amzn/meridian/checkbox';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useAppSelector } from 'src/store/store';
import { TaskTableDataRow } from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { ErrorRecord, HandleChange } from 'src/hooks/useForm';
import { createSearchRegExp } from 'src/utils/searchUtils';

// **** Types **** //

interface TaskModalFormProps {
  formData: TaskTableDataRow;
  errors: ErrorRecord<TaskTableDataRow>;
  handleChange: HandleChange<TaskTableDataRow>;
}

// **** Components **** //

/**
 * Default Component
 */
const TaskModalForm = ({ formData, errors, handleChange }: TaskModalFormProps) => {
  const { regions, unitsOfMeasure } = useAppSelector((state) => state.taskConfigPage);
  const [searchQuery, setSearchQuery] = useState();
  const searchRegExp = createSearchRegExp(searchQuery);
  const matchedRegionOptions = regions
    .map((region) => ({ label: region.name, value: region.name }))
    .filter((option) => !searchQuery || searchRegExp.test(option.label));

  return (
    <Box>
      <Row widths="fill" spacingInset="400 none">
        <Text>Task Name</Text>
        <Input
          type="text"
          placeholder="Enter task name..."
          value={formData.label}
          onChange={(v) => handleChange({ label: v })}
          error={errors.label?.error}
          errorMessage={errors.label?.errorMessage}
        />
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Region/State/Province</Text>
        <Select
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Select region..."
          value={formData.regions}
          onChange={(v) => handleChange({ regions: v })}
          error={errors.regions?.error}
          errorMessage={errors.regions?.errorMessage}
        >
          {matchedRegionOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Identify Body Location (L,R,B)?</Text>
        <Checkbox
          checked={formData.identifyBodyLocation === true}
          onChange={(value) => {
            handleChange({ identifyBodyLocation: value }, (identifyBodyLocation) => !!identifyBodyLocation);
          }}
        />
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Unit of Measure</Text>
        <Select
          placeholder="Select unit of measure..."
          value={formData.isMeasuredBy}
          onChange={(isMeasuredBy) => handleChange({ isMeasuredBy })}
          error={errors.isMeasuredBy?.error}
          errorMessage={errors.isMeasuredBy?.errorMessage}
        >
          {unitsOfMeasure.map((option) => (
            <SelectOption label={option.name} value={option.name} key={option.id} />
          ))}
        </Select>
      </Row>
      <Row widths="fill" spacingInset="400 none">
        <Text>Description</Text>
        <Input
          placeholder="Enter description..."
          value={formData.description}
          onChange={(v) => handleChange({ description: v })}
          error={errors.description?.error}
          errorMessage={errors.description?.errorMessage}
          type="text"
        />
      </Row>
    </Box>
  );
};

// **** Export **** //

export default TaskModalForm;
